import type { EntityKind, SchemaFact } from "../Dashboard/types";
import { useRmsBootstrap } from "./bootstrap";

const ENTITY_KINDS_TO_CHECK: EntityKind[] = [
  "manager",
  "vehicle",
  "company",
  "person",
  "client",
  "portfolio",
  "topic",
  "deal",
];

/**
 * A field a person can fill in when creating a record. The system columns
 * every entity schema ends with (Date Created, Created By, Last Updated At/By,
 * the Last Interaction family, #10497) are written by the system, so they are
 * not: a kind holding only those has nothing to create (#12224). The creation
 * form shows exactly these fields, so it and the gate below cannot disagree.
 */
export const isAuthorableFact = (fact: SchemaFact): boolean =>
  fact.provenance?.kind !== "derived" && fact.provenance?.kind !== "readonly";

/**
 * Hook to determine which entity types have authorable facts defined and
 * should be visible.
 * Reads every schema slice out of the shared RMS bootstrap cache — no per-kind
 * fetch. The bootstrap endpoint includes every kind listed here.
 */
export const useEntityTypeVisibility = (options?: { enabled?: boolean }) => {
  const { data: entityTypesWithFacts = new Set<EntityKind>(), ...rest } =
    useRmsBootstrap<Set<EntityKind>>({
      enabled: options?.enabled,
      select: (data) => {
        const set = new Set<EntityKind>();
        for (const kind of ENTITY_KINDS_TO_CHECK) {
          const schema = data.schemas[kind];
          if (schema && schema.facts.some(isAuthorableFact)) set.add(kind);
        }
        return set;
      },
    });

  return {
    entityTypesWithFacts,
    isLoading: rest.isLoading,
    hasError: rest.isError,
    /**
     * Check if an entity type should be visible (has authorable facts).
     * Exception: Interactions are always visible since they never have facts.
     */
    shouldShowEntity: (entityKind: EntityKind): boolean =>
      entityKind === "interaction" || entityTypesWithFacts.has(entityKind),
  };
};
